import { Controller } from "@hotwired/stimulus"

// Replays simplified agent workflows in the marketing hero, rotating through
// one scene per agent tab. The markup ships with every scene in its completed
// state, so no-JS and reduced-motion visitors see the finished story (and the
// tabs still switch scenes); this controller rewinds the active scene and
// plays its sequence while the demo is on screen.
export default class extends Controller {
  static targets = ["scene", "tab", "status"]

  // The demo lays out at a fixed design width and scales down as one unit so
  // it fits narrow containers without reflowing (see .hero-demo-viewport).
  // The width itself is set in CSS — narrower on phones so the scaled-down
  // text stays legible — and read back here via offsetWidth, which the scale
  // transform does not affect.
  static fallbackDesignWidth = 896

  connect() {
    this.timeouts = []
    this.sceneIndex = 0
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    this.resizeObserver = new ResizeObserver(() => this.rescale())
    const frame = this.element.closest(".hero-demo-frame")
    if (frame) this.resizeObserver.observe(frame)
    this.resizeObserver.observe(this.element)
    // ResizeObserver won't fire on window-height-only changes.
    this.onWindowResize = () => this.rescale()
    window.addEventListener("resize", this.onWindowResize)
    this.rescale()

    if (this.reducedMotion) return

    this.element.classList.add("hero-demo--animated")
    this.inView = false
    // Pause when the demo is off screen or the tab is hidden — background
    // timer throttling would otherwise bunch up steps and replay them in a
    // burst when the visitor comes back.
    this.onVisibility = () => this.syncPlayback()
    document.addEventListener("visibilitychange", this.onVisibility)
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => (this.inView = entry.isIntersecting))
        this.syncPlayback()
      },
      { threshold: 0.3 }
    )
    this.observer.observe(this.element)
  }

  disconnect() {
    this.stop()
    this.resizeObserver?.disconnect()
    window.removeEventListener("resize", this.onWindowResize)
    this.observer?.disconnect()
    document.removeEventListener("visibilitychange", this.onVisibility)
    this.element.classList.remove("hero-demo--animated")
  }

  rescale() {
    const viewport = this.element.parentElement
    const frame = this.element.closest(".hero-demo-frame") || viewport.parentElement
    const frameStyles = getComputedStyle(frame)
    const availableWidth =
      frame.clientWidth - parseFloat(frameStyles.paddingLeft) - parseFloat(frameStyles.paddingRight)
    if (!availableWidth) return

    const designWidth = this.element.offsetWidth || this.constructor.fallbackDesignWidth
    const naturalHeight = this.element.offsetHeight
    // Fit the whole demo on screen: cap the scale by the browser height as
    // well as the available width, so it shrinks instead of hanging off.
    // 65% leaves room for the nav above and a peek of the next section below.
    // Phones stack the side panel, so the demo is much taller relative to its
    // width — hold to the same cap there and the height, not the width, would
    // decide the scale and shrink the text back to unreadable.
    const heightAllowance = window.innerWidth < 768 ? 0.95 : 0.65
    const availableHeight = window.innerHeight * heightAllowance
    const scale = Math.min(availableWidth / designWidth, availableHeight / naturalHeight, 1)

    this.element.style.transform = scale < 1 ? `scale(${scale})` : ""
    viewport.style.width = `${designWidth * scale}px`
    viewport.style.height = `${naturalHeight * scale}px`
  }

  syncPlayback() {
    if (this.inView && !document.hidden) {
      this.start()
    } else {
      this.stop()
    }
  }

  select(event) {
    const index = Number(event.params.index)
    if (index === this.sceneIndex) return

    // Switch scenes instantly on manual selection — the crossfade plus the
    // scene reset reads as a confusing double-fade when user-initiated.
    this.element.classList.add("hero-demo--instant")
    if (this.reducedMotion) {
      this.activate(index)
    } else {
      this.playScene(index)
    }
    requestAnimationFrame(() =>
      requestAnimationFrame(() => this.element.classList.remove("hero-demo--instant"))
    )
  }

  start() {
    if (this.playing) return
    this.playing = true
    this.playScene(this.sceneIndex)
  }

  stop() {
    this.playing = false
    this.timeouts.forEach(clearTimeout)
    this.timeouts = []
  }

  // One timeline per scene: [delay, step] pairs plus the total duration
  // before auto-advancing to the next scene. Durations leave the finished
  // state on screen for ~7s so the ending can actually be read.
  timelines() {
    return [
      {
        duration: 15200,
        steps: [
          [400, (s) => this.showEvent(s, 0)],
          [2100, (s) => { this.showEvent(s, 1); this.checkObjective(s, 0); this.revealFields(s) }],
          [3900, (s) => { this.showEvent(s, 2); this.setStatus("review", "Waiting on review") }],
          [5900, (s) => this.approveButton(s)?.classList.add("is-clicking")],
          [6350, (s) => {
            this.approveButton(s)?.classList.remove("is-clicking")
            this.approveButton(s)?.classList.add("is-approved")
            this.checkObjective(s, 1)
          }],
          [7700, (s) => { this.showEvent(s, 3); this.checkObjective(s, 2); this.setStatus("done", "Completed") }]
        ]
      },
      {
        duration: 15000,
        steps: [
          [400, (s) => this.showEvent(s, 0)],
          [2000, (s) => { this.showEvent(s, 1); this.checkObjective(s, 0); this.revealFields(s) }],
          [3800, (s) => { this.showEvent(s, 2); this.checkObjective(s, 1) }],
          [5800, (s) => this.showEvent(s, 3)],
          [7700, (s) => { this.showEvent(s, 4); this.checkObjective(s, 2); this.setStatus("done", "Completed") }]
        ]
      },
      {
        duration: 13600,
        steps: [
          [400, (s) => { this.showEvent(s, 0); this.checkObjective(s, 0) }],
          [2100, (s) => { this.showEvent(s, 1); this.checkObjective(s, 1) }],
          [4000, (s) => { this.showEvent(s, 2); this.checkObjective(s, 2); this.revealFields(s) }],
          [6100, (s) => { this.showEvent(s, 3); this.setStatus("done", "Completed") }]
        ]
      },
      {
        duration: 13500,
        steps: [
          [400, (s) => this.showEvent(s, 0)],
          [2000, (s) => { this.showEvent(s, 1); this.checkObjective(s, 0); this.revealFields(s) }],
          [3900, (s) => { this.showEvent(s, 2); this.checkObjective(s, 1) }],
          [6000, (s) => { this.showEvent(s, 3); this.checkObjective(s, 2); this.setStatus("done", "Completed") }]
        ]
      },
      {
        duration: 13800,
        steps: [
          [400, (s) => { this.showEvent(s, 0); this.checkObjective(s, 0) }],
          [2100, (s) => this.showEvent(s, 1)],
          [4100, (s) => { this.showEvent(s, 2); this.checkObjective(s, 1); this.revealFields(s) }],
          [6300, (s) => { this.showEvent(s, 3); this.checkObjective(s, 2); this.setStatus("done", "Completed") }]
        ]
      }
    ]
  }

  playScene(index) {
    this.stop()
    this.playing = true
    this.activate(index)

    const scene = this.sceneTargets[index]
    const timeline = this.timelines()[index]
    if (!scene || !timeline) return

    this.resetScene(scene)
    timeline.steps.forEach(([delay, step]) => this.schedule(delay, () => step(scene)))
    this.schedule(timeline.duration, () => this.playScene((index + 1) % this.sceneTargets.length))
  }

  activate(index) {
    this.sceneIndex = index
    this.sceneTargets.forEach((scene, i) => scene.classList.toggle("is-active", i === index))
    this.tabTargets.forEach((tab, i) => {
      if (i === index) {
        tab.setAttribute("aria-current", "true")
      } else {
        tab.removeAttribute("aria-current")
      }
    })
  }

  schedule(delay, fn) {
    this.timeouts.push(setTimeout(fn, delay))
  }

  resetScene(scene) {
    scene.querySelectorAll(".hero-demo-event").forEach((el) => el.classList.remove("is-shown"))
    scene.querySelectorAll(".hero-demo-objective").forEach((el) => el.classList.remove("is-done"))
    scene.querySelectorAll(".hero-demo-field").forEach((el) => {
      el.classList.remove("is-shown")
      el.style.transitionDelay = ""
    })
    this.approveButton(scene)?.classList.remove("is-approved", "is-clicking")
    this.setStatus("open", "Open")
  }

  showEvent(scene, index) {
    scene.querySelectorAll(".hero-demo-event")[index]?.classList.add("is-shown")
  }

  checkObjective(scene, index) {
    scene.querySelectorAll(".hero-demo-objective")[index]?.classList.add("is-done")
  }

  revealFields(scene) {
    scene.querySelectorAll(".hero-demo-field").forEach((el, index) => {
      el.style.transitionDelay = `${index * 170}ms`
      el.classList.add("is-shown")
    })
  }

  approveButton(scene) {
    return scene.querySelector(".hero-demo-btn--primary")
  }

  setStatus(state, label) {
    this.statusTarget.dataset.state = state
    this.statusTarget.textContent = label
  }
}
